import {
  LeftOutlined,
  RightOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import { Popconfirm, Button, message, Flex, Row, Col } from 'antd';
import { ReactNode } from 'react';
import { Link, generatePath, useNavigate } from 'react-router-dom';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { reportIsValidated } from 'business/shift-report/report/pages/ReportView/services';
import Routes from 'config/routes';
import {
  ConstructionSiteFieldsFragment,
  GetShiftReportMetadataQuery,
  GetShiftReportPdfMetadataQuery,
  Module_Enum,
  useInvalidateManagerValidationMutation,
} from 'generated/graphql';
import logger from 'technical/logger';
import { useMediaType } from 'technical/media/hooks';
import { GoBackButton } from 'ui/button/go-back-button';
import { PrintButton } from 'ui/button/print-button';

const handlePrint = () => {
  window.print();
};

type ControlsContainerProps = {
  redirect: string;
  filters: string | null;
  report: NonNullable<
    | GetShiftReportPdfMetadataQuery['shiftReport']
    | GetShiftReportMetadataQuery['shiftReport']
  >;
  currentConstructionSite: ConstructionSiteFieldsFragment | null;
  hasManagerEditPermission: boolean;
  previousReportId: string | undefined;
  nextReportId: string | undefined;
};

const ReportNavigationButton = ({
  reportId,
  searchParams,
  icon,
}: {
  reportId: string | undefined;
  searchParams: string;
  icon: ReactNode;
}) => {
  if (!reportId) {
    return <Button type="primary" disabled icon={icon} />;
  }

  return (
    <Link
      to={`${generatePath(Routes.ReportView, { id: reportId })}?${searchParams}`}
    >
      <Button type="primary" icon={icon} />
    </Link>
  );
};

export const ControlsContainer = ({
  redirect,
  filters,
  report,
  currentConstructionSite,
  hasManagerEditPermission,
  previousReportId,
  nextReportId,
}: ControlsContainerProps) => {
  const { isMobile, isTablet } = useMediaType();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const graphqlHeaders = useGraphqlApiHeaders(Module_Enum.ShiftReport);
  const [invalidateManagerValidation] = useInvalidateManagerValidationMutation({
    context: { headers: graphqlHeaders },
  });

  const handleInvalidateManagerValidation = async () => {
    const validationOptions = currentConstructionSite?.managerValidationRequired
      ? {
          managerValidation: false,
          operatorValidation: true,
        }
      : {
          managerValidation: false,
          operatorValidation: false,
        };

    try {
      const { errors: invalidateErrors } = await invalidateManagerValidation({
        variables: { id: report.id, ...validationOptions },
      });
      if (invalidateErrors) {
        message.error(t('pages.print.invalidateShiftReportFailed'));
        logger.error(invalidateErrors);
      } else {
        message.success(t('pages.print.invalidateShiftReportSuccess'));
        navigate(generatePath(Routes.ReportEdit, { id: report.id }));
      }
    } catch (err) {
      message.error(t('pages.print.invalidateShiftReportFailed'));
      logger.error(err);
    }
  };

  const filtersSearchParam = filters ? decodeURIComponent(filters) : '';
  const backRouteSearchParams = new URLSearchParams({
    redirect,
    filters: filtersSearchParam,
  }).toString();
  const backRoute = `${redirect}?${filtersSearchParam}`;

  return (
    <Row className="controls">
      <Col span={10}>
        <GoBackButton to={backRoute} isMobile={isMobile} />
      </Col>

      <Col span={isMobile ? 14 : 4}>
        <Flex gap={8} justify={isMobile ? 'end' : 'center'}>
          <ReportNavigationButton
            reportId={previousReportId}
            searchParams={backRouteSearchParams}
            icon={<LeftOutlined />}
          />
          <ReportNavigationButton
            reportId={nextReportId}
            searchParams={backRouteSearchParams}
            icon={<RightOutlined />}
          />
        </Flex>
      </Col>

      {!isMobile ? (
        <Col span={10}>
          <Flex gap={8} justify="end">
            {reportIsValidated(report, currentConstructionSite) &&
              hasManagerEditPermission &&
              !currentConstructionSite?.isCompleted && (
                <Popconfirm
                  title={t('pages.print.invalidateManagerValidationHelper')}
                  onConfirm={handleInvalidateManagerValidation}
                >
                  <Button icon={<RollbackOutlined />}>
                    {t('pages.print.invalidateManagerValidationButton')}
                  </Button>
                </Popconfirm>
              )}
            {reportIsValidated(report, currentConstructionSite) &&
              !isTablet && <PrintButton onClick={handlePrint} type="primary" />}
          </Flex>
        </Col>
      ) : null}
    </Row>
  );
};
